import { BaseCommand, type CommandResult } from './BaseCommand';
import { goto } from '$app/navigation';
import AdminService from '$lib/services/AdminService';
import { queryCollection } from '$lib/firebase/firestore';

interface SearchResult {
  userId: string;
  field: string;
  email: string;
  name: string;
}

export interface ActionItem {
  message: string;
  action: (event: Event, onAction?: () => void) => void;
}

export class SearchCommand extends BaseCommand {
  private searchResults: SearchResult[] = [];

  getKeywords(): string[] {
    return ['search'];
  }

  parameterDescriptions = [
    { key: 'query', description: 'The search term to look for' },
    { key: 'filter', description: 'Optional filter to apply to search results (users, content, etc)' },
    { key: 'fields', description: 'Optional comma-separated list of fields to search (email,name,mollieCustomerId)' },
  ];

  async action(
    parameters: string[],
    onMessage?: (message: string) => void,
  ): Promise<CommandResult> {
    const query = this.parameters[0];
    const filter = this.parameters[1]?.toLowerCase();
    const fieldsInput = this.parameters[2];

    if (!query) {
      return Promise.reject(new Error('Search query is required'));
    }

    let fields: string[] | undefined;
    if (fieldsInput) {
      fields = fieldsInput.split(',').map((f) => f.trim());
    } else {
      fields = ['email', 'name', 'mollieCustomerId'];
    }

    return new Promise(async (resolve, reject) => {
      try {
        onMessage?.('Searching users and organizations...');

        const [userResults, orgSnap] = await Promise.all([
          AdminService.searchUsers(query, fields?.join(',')).catch(() => []),
          queryCollection<any>('organizations', []).catch(() => null),
        ]);

        this.searchResults = userResults;

        const actionItems: ActionItem[] = [];

        // Add matching users
        userResults.forEach((result: SearchResult) => {
          const displayName = result.name || 'Unknown User';
          const email = result.email || 'No email';
          actionItems.push({
            message: `👤 ${displayName} (${email})`,
            action: (event: Event, onAction?: () => void) => {
              this.navigateToEmail(email, (event as MouseEvent).metaKey);
              onAction?.();
            },
          });
        });

        // Add matching organizations
        if (orgSnap) {
          const lowerQuery = query.toLowerCase();
          orgSnap.docs.forEach((doc) => {
            const data = doc.data();
            const orgName = data.name || '';
            const adminEmails: string[] = data.adminEmails || [];
            if (
              orgName.toLowerCase().includes(lowerQuery) ||
              adminEmails.some((e) => e.toLowerCase().includes(lowerQuery))
            ) {
              actionItems.push({
                message: `🏢 Organization: ${orgName} (${data.seats || 0} seats)`,
                action: (event: Event, onAction?: () => void) => {
                  const url = `/admin/organizations`;
                  if ((event as MouseEvent).metaKey) {
                    window.open(url, '_blank');
                  } else {
                    goto(url);
                  }
                  onAction?.();
                },
              });
            }
          });
        }

        if (actionItems.length === 0) {
          onMessage?.('No results found');
          return;
        }

        resolve(actionItems);
      } catch (error) {
        if (error instanceof Error) {
          onMessage?.(error.message);
        }
        reject(error);
      }
    });
  }

  navigateToEmail(email: string, newTab = false) {
    const url = `/admin/user/${encodeURIComponent(email)}`;
    if (newTab) {
      window.open(url, '_blank');
    } else {
      goto(url);
    }
  }

  navigateToResult(index: number, newTab = false) {
    if (index < 0 || index >= this.searchResults.length) {
      return;
    }
    const result = this.searchResults[index];
    const url = `/admin/user/${encodeURIComponent(result.email)}`;
    if (newTab) {
      window.open(url, '_blank');
    } else {
      goto(url);
    }
  }
}
